import React, { useMemo, useState } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  BarChart3,
  CheckCircle,
  AlertCircle,
  FileText,
  Calendar,
} from 'lucide-react';
import type { ModuleElement } from '@/types/filiere.types';
import type { ExamPeriods } from '../../services/examScheduleService';

interface ExamSchedulingProgressPanelProps {
  matieres: ModuleElement[];
  scheduledExamsMap: Map<string, number>;
  groupName?: string;
  semester: 1 | 2;
  examPeriods: ExamPeriods | null;
}

type ExamStatus = 'NOT_SCHEDULED' | 'SCHEDULED';

interface ModuleExamProgress {
  matiere: ModuleElement;
  examCount: number;
  status: ExamStatus;
}

export const ExamSchedulingProgressPanel: React.FC<ExamSchedulingProgressPanelProps> = ({
  matieres,
  scheduledExamsMap,
  groupName,
  semester,
  examPeriods,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Calculate progress for each module
  const moduleProgress = useMemo((): ModuleExamProgress[] => {
    return matieres.map((matiere) => {
      const examCount = scheduledExamsMap.get(matiere.id) || 0;
      const status: ExamStatus = examCount > 0 ? 'SCHEDULED' : 'NOT_SCHEDULED';

      return {
        matiere,
        examCount,
        status,
      };
    });
  }, [matieres, scheduledExamsMap]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalModules = moduleProgress.length;
    const modulesScheduled = moduleProgress.filter((m) => m.status === 'SCHEDULED').length;
    const modulesNotScheduled = moduleProgress.filter((m) => m.status === 'NOT_SCHEDULED').length;
    const totalExams = moduleProgress.reduce((sum, m) => sum + m.examCount, 0);

    const percentage = totalModules > 0 ? (modulesScheduled / totalModules) * 100 : 0;

    return {
      totalModules,
      modulesScheduled,
      modulesNotScheduled,
      totalExams,
      percentage,
    };
  }, [moduleProgress]);

  // Get exam period info for current semester
  const examPeriodInfo = useMemo(() => {
    if (!examPeriods) return null;
    const period = semester === 1 ? examPeriods.period1 : examPeriods.period2;
    if (!period || !period.start || !period.end) return null;
    return {
      start: new Date(period.start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      end: new Date(period.end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      deliberation: null, // Removed deliberation as it's not in the new type
    };
  }, [examPeriods, semester]);

  const getStatusIcon = (status: ExamStatus) => {
    switch (status) {
      case 'SCHEDULED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'NOT_SCHEDULED':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 bg-white border border-gray-200 rounded-l-lg shadow-lg p-2 hover:bg-gray-50 transition-colors z-40"
        title="Ouvrir le panneau de progression"
      >
        <div className="flex flex-col items-center gap-2">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
          <BarChart3 className="w-5 h-5 text-purple-600" />
          <span className="text-xs font-medium text-gray-600 writing-mode-vertical">
            Examens
          </span>
        </div>
      </button>
    );
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Progression Examens</h3>
            {groupName && <p className="text-xs text-gray-500">{groupName} - S{semester}</p>}
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Réduire le panneau"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Exam Period Info */}
      {examPeriodInfo && (
        <div className="px-4 py-3 border-b border-gray-100 bg-purple-50">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-800">Période d'examens S{semester}</span>
          </div>
          <p className="text-xs text-purple-700">
            {examPeriodInfo.start} - {examPeriodInfo.end}
          </p>
          {examPeriodInfo.deliberation && (
            <p className="text-[10px] text-purple-600 mt-0.5">
              Délibération: {examPeriodInfo.deliberation}
            </p>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="px-4 py-3 border-b border-gray-100 space-y-3">
        {/* Overall Progress */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-600">Matières avec examen</span>
            <span className="text-xs font-semibold text-gray-900">
              {summary.modulesScheduled}/{summary.totalModules}
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-600 rounded-full transition-all duration-300"
              style={{ width: `${summary.percentage}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {summary.totalExams} examen{summary.totalExams !== 1 ? 's' : ''} planifié{summary.totalExams !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-2 gap-2">
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <p className="text-lg font-bold text-green-700">{summary.modulesScheduled}</p>
            <p className="text-xs text-green-600">Planifié</p>
          </div>
          <div className="text-center p-2 bg-gray-50 rounded-lg">
            <p className="text-lg font-bold text-gray-700">{summary.modulesNotScheduled}</p>
            <p className="text-xs text-gray-600">Non planifié</p>
          </div>
        </div>
      </div>

      {/* Module List */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Matières ({summary.totalModules})
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {moduleProgress.map((item) => (
            <div
              key={item.matiere.id}
              className="px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {getStatusIcon(item.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {item.matiere.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.examCount > 0 ? (
                      <span className="text-xs text-green-600">
                        {item.examCount} examen{item.examCount > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Non planifié</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {moduleProgress.length === 0 && (
            <div className="px-4 py-8 text-center">
              <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Aucune matière à planifier</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamSchedulingProgressPanel;
